import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../api/client';
import styles from './Dialog.module.css';

interface Template {
  templateId: number;
  name: string;
  modality?: string;
  bodyPart?: string;
  updatedAt?: string;
}

interface Props {
  onClose: () => void;
  /** Called after any mutation (rename / delete / new) so parent can refresh its list */
  onChanged: () => void;
}

export default function ManageTemplatesDialog({ onClose, onChanged }: Props) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Template | null>(null);
  const [newName, setNewName] = useState('');
  const [newModality, setNewModality] = useState('');
  const [newBodyPart, setNewBodyPart] = useState('');

  async function refresh() {
    setLoading(true);
    const res = await apiCall<Template>('templates.list', { modality: '' });
    setTemplates(res.s === 'ok' ? (res.data || []) : []);
    setLoading(false);
  }

  useEffect(() => { refresh(); }, []);

  function startEdit(t: Template) {
    setEditing(t);
    setNewName(t.name);
    setNewModality(t.modality || '');
    setNewBodyPart(t.bodyPart || '');
  }

  async function saveMeta() {
    if (!editing || !newName.trim()) return;
    // Uniqueness check (case-insensitive), excluding the template being renamed.
    const nameLower = newName.trim().toLowerCase();
    const collision = templates.find(
      (t) => t.templateId !== editing.templateId && (t.name || '').trim().toLowerCase() === nameLower,
    );
    if (collision) {
      alert(`A template named "${newName.trim()}" already exists. Pick a unique name.`);
      return;
    }
    // Fetch existing content so we can round-trip it back (backend overwrites on save).
    // A dedicated "rename" endpoint would be cleaner; this keeps the backend simple.
    const existing = await apiCall<{ content: string; contentType?: string }>(
      'templates.get', { templateId: editing.templateId }
    );
    const content = existing.s === 'ok' ? (existing.data?.[0]?.content ?? '') : '';
    const contentType = existing.s === 'ok' ? (existing.data?.[0]?.contentType ?? 'sfdt') : 'sfdt';

    const res = await apiCall('templates.save', {
      templateId: editing.templateId,
      name: newName.trim(),
      modality: newModality.trim().toUpperCase(),
      bodyPart: newBodyPart.trim(),
      content, contentType,
    });
    if (res.s === 'ok') {
      setEditing(null);
      await refresh();
      onChanged();
    } else {
      alert('Rename failed: ' + (res.error || ''));
    }
  }

  async function remove(t: Template) {
    if (!confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    const res = await apiCall('templates.delete', { templateId: t.templateId });
    if (res.s === 'ok') {
      await refresh();
      onChanged();
    } else {
      alert('Delete failed: ' + (res.error || ''));
    }
  }

  function editInApp(t: Template) {
    const guard = (window as any).__reportEditorCloseGuard;
    if (typeof guard === 'function' && !guard()) return;
    // Remember where the user was (typically the report editor) so
    // the Back button on TemplatePage returns them there.
    const from = window.location.pathname + window.location.hash;
    navigate(`/template/${t.templateId}`, { state: { from } });
  }

  function openNew() {
    const guard = (window as any).__reportEditorCloseGuard;
    if (typeof guard === 'function' && !guard()) return;
    const from = window.location.pathname + window.location.hash;
    navigate('/template/new', { state: { from } });
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={`${styles.dialog} ${styles.dialogLarge}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>Manage Templates</h3>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.body}>
          {editing ? (
            <>
              <label className={styles.label}>Name</label>
              <input
                className={styles.input}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <label className={styles.label}>Modality</label>
              <input
                className={styles.input}
                value={newModality}
                onChange={(e) => setNewModality(e.target.value.toUpperCase())}
              />
              <label className={styles.label}>Body Part</label>
              <input
                className={styles.input}
                value={newBodyPart}
                onChange={(e) => setNewBodyPart(e.target.value)}
              />
              <div style={{ fontSize: 11, color: '#718096', marginTop: 8 }}>
                To edit the template's actual content, click Cancel and use "Edit content".
              </div>
            </>
          ) : (
            <>
              {loading && <div>Loading…</div>}
              {!loading && templates.length === 0 && (
                <div className={styles.empty}>
                  No templates yet. Open a report, compose it, and click "Save as Template" — or create one from scratch below.
                </div>
              )}
              {templates.map((t) => (
                <div
                  key={t.templateId}
                  className={styles.listItem}
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <div style={{ flex: 1 }}>
                    <div className={styles.itemTitle}>{t.name}</div>
                    <div className={styles.itemSub}>
                      {t.modality || 'any modality'}
                      {t.bodyPart ? ` · ${t.bodyPart}` : ''}
                      {t.updatedAt ? ` · updated ${t.updatedAt.split(' ')[0]}` : ''}
                    </div>
                  </div>
                  <button className={styles.secondaryBtn} onClick={() => editInApp(t)}>
                    Edit content
                  </button>
                  <button className={styles.secondaryBtn} onClick={() => startEdit(t)}>
                    Rename
                  </button>
                  <button className={styles.dangerBtn} onClick={() => remove(t)}>
                    Delete
                  </button>
                </div>
              ))}
            </>
          )}
        </div>

        <div className={styles.footer}>
          {editing ? (
            <>
              <button className={styles.secondaryBtn} onClick={() => setEditing(null)}>Cancel</button>
              <button className={styles.primaryBtn} onClick={saveMeta} disabled={!newName.trim()}>
                Save
              </button>
            </>
          ) : (
            <>
              <button className={styles.secondaryBtn} onClick={onClose}>Close</button>
              <button className={styles.primaryBtn} onClick={openNew}>
                New Blank Template
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
